import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taxonomyApi } from './service';
import type {
  ArchiveTaxonomyPayload,
  CreateTaxonomyPayload,
  ReorderTaxonomyPayload,
  TaxonomyKind,
  UpdateTaxonomyPayload,
} from './types';

const FIVE_MIN = 5 * 60 * 1000;

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  byKind: (kind: TaxonomyKind, includeArchived: boolean) =>
    [...taxonomyKeys.all, 'list', kind, includeArchived] as const,
};

/**
 * useTaxonomy(kind) — primary consumer hook for UI dropdowns. By
 * default returns active (non-archived) entries sorted by
 * sortOrder. The 5-minute staleTime is enough for a dropdown that
 * almost never changes mid-session and avoids spamming the API as
 * the user opens forms.
 */
export function useTaxonomy(
  kind: TaxonomyKind,
  options: { includeArchived?: boolean } = {},
) {
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: taxonomyKeys.byKind(kind, includeArchived),
    queryFn: () => taxonomyApi.list(kind, { includeArchived }),
    staleTime: FIVE_MIN,
  });
}

export function useCreateTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaxonomyPayload) => taxonomyApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}

export function useUpdateTaxonomy(taxonomyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTaxonomyPayload) =>
      taxonomyApi.update(taxonomyId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}

export function useArchiveTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input?: ArchiveTaxonomyPayload;
    }) => taxonomyApi.archive(id, input ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}

export function useRestoreTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => taxonomyApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}

export function useReorderTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderTaxonomyPayload) => taxonomyApi.reorder(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}
